import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/apiConfig';
import CapaEffectivenessReport from './components/CapaEffectivenessReport';
import { Button } from '../../components/ui';
import { ArrowLeftIcon, PrinterIcon } from '../../components/ui/Icons';

/**
 * Standalone print page for CAPA Effectiveness Check.
 * Accessible via /capa-effectiveness-check/:id/print
 */
const CapaEffectivenessPrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');

        const response = await fetch(
          `${API_BASE_URL}/capa-effectiveness-check/${id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(userId ? { 'user-id': userId } : {}),
            },
          },
        );

        if (!response.ok) {
          throw new Error('Failed to fetch CAPA effectiveness check');
        }

        setRecord(await response.json());
      } catch (err) {
        console.error('Error fetching CAPA effectiveness check:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchRecord();
  }, [id]);

  const handlePrint = () => {
    setTimeout(() => window.print(), 100);
  };

  useEffect(() => {
    if (record && !loading && !error) {
      const timer = setTimeout(() => window.print(), 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [record, loading, error]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading report…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="text-lg text-red-600">Error: {error}</div>
        <Button onClick={() => navigate('/capa-effectiveness-check')}>
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="no-print bg-white border-b shadow-sm p-4 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/capa-effectiveness-check')}
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-semibold">
              CAPA Effectiveness Check — {record?.code}
            </h1>
          </div>
          <Button onClick={handlePrint}>
            <PrinterIcon className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      <div className="bg-gray-100 min-h-screen py-6">
        <CapaEffectivenessReport record={record} />
      </div>
    </div>
  );
};

export default CapaEffectivenessPrintPage;
